// components/auth/AccountInactivePage.js
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { 
  AlertTriangle, 
  Mail, 
  Phone, 
  MessageCircle,
  LogOut,
  Home,
  RefreshCw
} from 'lucide-react';
import tunaLogo from '../../assets/tunasignalai_logo.png';
import { SiteStyles } from '../site/SiteChrome';

const AccountInactivePage = () => {
  useTheme();
  const darkMode = true; // public and account pages always use the site's dark terminal theme
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleEmailSupport = () => {
    window.location.href = 'mailto:support@tunasignal.ai?subject=Account%20Inactive%20Issue';
  };

  return (
    <div className="dark ts-page">
    <SiteStyles />
    <div className={`min-h-screen ${
      darkMode ? 'bg-[#07131F]' : 'bg-slate-50 dark:bg-[#13283C]'
    }`}>
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className={`w-full max-w-2xl ${
          darkMode ? 'bg-[#0D1E2E]' : 'bg-white'
        } rounded-md overflow-hidden`}>
          
          {/* Warning Header */}
          <div className="relative bg-[#0D1E2E] border-b border-[#1C3348] p-8 text-white">
            
            <div className="relative">
              <AlertTriangle className="w-7 h-7 mb-3 text-amber-400" aria-hidden="true" />
              <h1 className="text-2xl font-semibold mb-1">Account Inactive</h1>
              <p className="text-slate-400">
                Your account is currently inactive
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-8">
            {/* Reason Section */}
            <div className={`rounded-lg p-6 mb-6 ${
              darkMode ? 'bg-[#13283C]' : 'bg-gray-50'
            }`}>
              <h2 className={`text-lg font-semibold mb-3 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Why is my account inactive?
              </h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Your account may be inactive for one of the following reasons:
              </p>
              <ul className="space-y-2">
                <li className={`flex items-start ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>Your subscription has expired and needs renewal</span>
                </li>
                <li className={`flex items-start ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>Account verification is still in progress</span>
                </li>
                <li className={`flex items-start ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>Administrative action requiring your attention</span>
                </li>
                <li className={`flex items-start ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span className="text-yellow-500 mr-2">•</span>
                  <span>Payment method needs to be updated</span>
                </li>
              </ul>
            </div>

            {/* Contact Support Section */}
            <div className={`rounded-lg border ${
              darkMode ? 'border-[#1C3348]' : 'border-gray-200'
            } p-6 mb-6`}>
              <h3 className={`font-semibold mb-4 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                How to Reactivate Your Account
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <Mail className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
                  <div>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Email Support
                    </p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Contact us at support@tunasignal.ai for immediate assistance
                    </p>
                    <button
                      onClick={handleEmailSupport}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium mt-2"
                    >
                      Send Email →
                    </button>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <MessageCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                  <div>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Live Chat
                    </p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Available Monday-Friday, 9 AM - 5 PM EST
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Phone className="w-5 h-5 text-slate-400 mr-3 mt-0.5" />
                  <div>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Response Time
                    </p>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      We typically respond within 24 hours
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleRefresh}
                className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center ${
                  darkMode 
                    ? 'bg-[#5AA9F0] hover:bg-[#8CC4F5] text-[#07131F]' 
                    : 'bg-[#5AA9F0] hover:bg-[#8CC4F5] text-[#07131F]'
                }`}
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Check Account Status Again
              </button>
              
              <button
                onClick={() => navigate('/')}
                className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center border ${
                  darkMode 
                    ? 'border-[#28445F] hover:bg-[#13283C] text-gray-300' 
                    : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                }`}
              >
                <Home className="w-5 h-5 mr-2" />
                Return to Home
              </button>
              
              <button
                onClick={handleLogout}
                className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center ${
                  darkMode 
                    ? 'text-red-400 hover:bg-red-900/20' 
                    : 'text-red-600 hover:bg-red-50'
                }`}
              >
                <LogOut className="w-5 h-5 mr-2" />
                Sign Out
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className={`px-8 py-4 border-t ${
            darkMode ? 'bg-[#07131F] border-[#1C3348]' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <img src={tunaLogo} alt="TunaSignal AI" className="h-6" />
              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Account ID: {localStorage.getItem('user_id') || 'Not available'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default AccountInactivePage;